import React, { FC, useEffect, useMemo, useState } from 'react';
import { Button, Skeleton } from 'antd';
import { InfoTooltip } from 'common/components/InfoTooltip';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getMonitorSalesOosModel } from 'app/store/reducers/monitorSales.reducer';
import {
  IMonitorSalesOosItem,
  IMonitorSalesParams,
  IOosOpportunityLossCategoryGroup,
} from 'entities/MonitorSales/MonitorSales.models';
import { CategoryColorLegend } from 'entities/MonitorSales/components/CategoryColorLegend';
import { OosOpportunityLossModal } from 'entities/MonitorSales/components/OosOpportunityLossModal';
import { OosOpportunityLossCategoryGroup } from 'entities/MonitorSales/components/OosOpportunityLossCategoryGroup';

const MAX_CATEGORIES = 2;
const MAX_PRODUCTS_PER_CATEGORY = 4;

// ─── Grouping ─────────────────────────────────────────────────────────────────

interface ICategoryGroupWithTotal extends IOosOpportunityLossCategoryGroup {
  total: number;
}

// Groups items by category; each group's items are sorted by potentialSalesLost desc
// and the group carries its total potentialSalesLost (used to rank categories later).
function groupByCategory(items: IMonitorSalesOosItem[]): ICategoryGroupWithTotal[] {
  const itemsByCategory = new Map<string, IMonitorSalesOosItem[]>();

  items.forEach((item) => {
    const categoryItems = itemsByCategory.get(item.category) ?? [];
    categoryItems.push(item);
    itemsByCategory.set(item.category, categoryItems);
  });

  return Array.from(itemsByCategory.entries()).map(([category, categoryItems]) => ({
    category,
    total: categoryItems.reduce((sum, item) => sum + item.potentialSalesLost, 0),
    items: [...categoryItems].sort((a, b) => b.potentialSalesLost - a.potentialSalesLost),
  }));
}

// Keeps only the categories with the highest total potentialSalesLost, and within each,
// only its top products (both already sorted by groupByCategory).
function limitTopCategories(groups: ICategoryGroupWithTotal[]): IOosOpportunityLossCategoryGroup[] {
  return [...groups]
    .sort((a, b) => b.total - a.total)
    .slice(0, MAX_CATEGORIES)
    .map(({ category, items }) => ({ category, items: items.slice(0, MAX_PRODUCTS_PER_CATEGORY) }));
}

// ─── Block ────────────────────────────────────────────────────────────────────

interface IComponentProps {
  params?: IMonitorSalesParams;
}

export const OosOpportunityLossBlock: FC<IComponentProps> = ({ params }) => {
  const dispatch = useAppDispatch();
  const { monitorSalesOosModel, monitorSalesOosModelLoading } = useAppSelector((state) => state.monitorSales);

  const [openOosOpportunityLossModal, setOpenOosOpportunityLossModal] = useState<boolean>(false);

  useEffect(() => {
    dispatch(getMonitorSalesOosModel(params));
  }, [params?.storeCode, params?.week]);

  const oosItems = monitorSalesOosModel?.items ?? [];
  const categoryGroups = useMemo(() => groupByCategory(oosItems), [oosItems]);
  const limitedCategoryGroups = useMemo(() => limitTopCategories(categoryGroups), [categoryGroups]);
  const maxPotentialSalesLost = useMemo(
    () => Math.max(1, ...categoryGroups.flatMap((group) => group.items.map((item) => item.potentialSalesLost))),
    [categoryGroups]
  );

  const handleOpenOosOpportunityLossModal = (): void => {
    setOpenOosOpportunityLossModal(true);
  };

  const closeOosOpportunityLossModal = (): void => {
    setOpenOosOpportunityLossModal(false);
  };

  return (
    <div className="ms-block">
      <div className="ms__header">
        <div className="flex flex-align-center">
          <span className="mr-5 ff-medium fs-xs">OOS Opportunity Loss</span>
          <Button className="btn-secondary h-24" onClick={handleOpenOosOpportunityLossModal}>
            Show All
          </Button>
        </div>
        <InfoTooltip
          title={
            <div>
              <div className="mb-3 ff-medium fs-xxs">OOS Opportunity Loss</div>
              <div className="mb-4 ff-medium fs-xxxs color-gray-1">See how much you lost due to out-of-stock!</div>
              <CategoryColorLegend />
              <div className="mb-4 ff-medium fs-xxxs color-gray-1">Tap “Show All” to see all products.</div>
            </div>
          }
        />
      </div>

      <div className="ms__body">
        {monitorSalesOosModelLoading ? (
          <Skeleton active paragraph={{ rows: 6 }} title={false} />
        ) : (
          <div className="ms-oos">
            {limitedCategoryGroups.map((group) => (
              <OosOpportunityLossCategoryGroup
                key={group.category}
                categoryGroup={group}
                maxPotentialSalesLost={maxPotentialSalesLost}
              />
            ))}
          </div>
        )}
      </div>

      <OosOpportunityLossModal
        open={openOosOpportunityLossModal}
        categoryGroups={categoryGroups}
        maxPotentialSalesLost={maxPotentialSalesLost}
        onCancel={closeOosOpportunityLossModal}
      />
    </div>
  );
};
